import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, Signal, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DocsService } from '@docs/services/docs/docs.service';
import { PageKey } from '../../services/page/models/page.key';
import { PageService } from '../../services/page/page.service';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { TextComponent } from '../text/text.component';
import { Menu, MenuGroupView } from './models/menu';
import { MenuService } from './services/menu.service';

@Component({
  selector: 'l-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './menu.component.html',
  imports: [RouterLink, NgTemplateOutlet, IconComponent, NgOptimizedImage, NgClass, ButtonComponent, TextComponent],
  styles: `
    .menu-nav-scroll::-webkit-scrollbar {
      width: 0.2rem;
      height: 0.2rem;
    }

    .menu-nav-scroll::-webkit-scrollbar-thumb {
      background-color: #3542C9;
      border-radius: 3.1rem;
    }

    .menu-nav-scroll::-webkit-scrollbar-track {
      background-color: transparent;
      border-radius: 1rem;
    }
  `
})
export class MenuComponent {
  private readonly docsService = inject(DocsService);

  constructor(
    private readonly menuService: MenuService,
    private readonly pageService: PageService
  ) {}

  @Input({ required: true })
  public menus: Menu[] = [];

  public get menuGroups(): MenuGroupView[] {
    const groups: MenuGroupView[] = [];

    for (const menu of this.menus) {
      const label = menu.group ?? null;
      const last = groups.at(-1);

      if (last?.label !== label) {
        groups.push({ label, items: [menu] });
        continue;
      }

      last.items.push(menu);
    }

    return groups;
  }

  public isMenuActive(menu: Menu): boolean {
    const currentPageKey = this.pageKey();

    if (!currentPageKey || !menu.key) return false;

    return menu.key === currentPageKey;
  }

  public menuIndex(menu: Menu): number {
    return this.menus.indexOf(menu);
  }

  public mobileOpen: Signal<boolean> = this.menuService.mobileOpen;

  public pageKey: Signal<PageKey | null> = this.pageService.page;

  public closeMobileMenu(): void {
    this.menuService.mobileOpen = false;
  }

  public toggleMobileMenu(): void {
    this.menuService.toggle();
  }

  public toggleMenu(index: number): void {
    const menu = this.menus[index];

    if (!menu) return;

    this.menus[index].opened = !this.menus[index].opened;
  }

  public openDocs(): void {
    this.docsService.open();
  }
}
